import React, { useEffect, useId, useState } from 'react';
import {
  cadCentsToEditDraft,
  formatCadCentsForMoneyInput,
  formatCadDraftForMoneyInput,
  parseCadMoneyInputToCents,
} from '../../utils/publicCadMoney.js';

/**
 * Accessible annual CAD money input with familiar Rentalyzer focus/blur formatting.
 * Parent keeps an in-memory draft string; authoritative cents conversion for submit
 * uses parseCadMoneyInputToCents with the active language (blur-independent).
 */
export default function PublicProspectMoneyInput({
  id,
  label,
  helpText,
  placeholder,
  language = 'fr',
  value,
  onChange,
  disabled,
  error,
  errorId,
  unknownChecked = null,
  onUnknownChange = null,
  unknownLabel = null,
}) {
  const generatedId = useId();
  const inputId = id || generatedId;
  const helpId = `${inputId}-help`;
  const unknownId = `${inputId}-unknown`;
  const describedBy = [helpText ? helpId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  const unknownMode = typeof onUnknownChange === 'function';
  const invalid = Boolean(error);
  const [isFocused, setIsFocused] = useState(false);
  const [displayValue, setDisplayValue] = useState('');

  useEffect(() => {
    if (isFocused || unknownChecked === true) return;
    const draft = typeof value === 'string' ? value : value == null ? '' : String(value);
    if (!draft.trim()) {
      setDisplayValue('');
      return;
    }
    const parsed = parseCadMoneyInputToCents(draft, language);
    if (parsed.ok) {
      setDisplayValue(formatCadCentsForMoneyInput(parsed.cents, language));
    } else {
      setDisplayValue(draft);
    }
  }, [value, language, isFocused, unknownChecked]);

  const handleChange = (event) => {
    const raw = event.target.value;
    // Keep typing fluid: allow digits, separators, spaces, and currency decoration.
    const cleaned = raw.replace(/[^\d.,\s\u00a0\u202f$CAD]/gi, '');
    setDisplayValue(cleaned);
    onChange(cleaned);
  };

  const handleFocus = () => {
    setIsFocused(true);
    const draft = typeof value === 'string' ? value : value == null ? '' : String(value);
    const parsed = parseCadMoneyInputToCents(draft, language);
    if (parsed.ok) {
      setDisplayValue(cadCentsToEditDraft(parsed.cents, language));
    } else {
      setDisplayValue(draft);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    const formatted = formatCadDraftForMoneyInput(displayValue, language);
    onChange(formatted.draft);
    setDisplayValue(formatted.display);
  };

  return (
    <div className="space-y-2">
      <label className="block" htmlFor={inputId}>
        <span className="font-medium text-[#004747]">{label}</span>
        {helpText ? (
          <span id={helpId} className="mt-1 block text-sm text-[#1f4a52]">
            {helpText}
          </span>
        ) : null}
        <input
          id={inputId}
          className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-base text-[#1f4a52] focus:border-[#004747] focus:outline-none focus:ring-2 focus:ring-[#007c91] disabled:bg-gray-100 disabled:opacity-70"
          inputMode="decimal"
          autoComplete="off"
          placeholder={placeholder}
          value={unknownChecked ? '' : displayValue}
          disabled={disabled || unknownChecked === true}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </label>
      {unknownMode ? (
        <label
          className="inline-flex min-h-[2.75rem] items-center gap-3 text-sm leading-5 text-[#1f4a52]"
          htmlFor={unknownId}
        >
          <input
            id={unknownId}
            className="m-0 h-5 w-5 shrink-0"
            type="checkbox"
            checked={unknownChecked === true}
            disabled={disabled}
            aria-invalid={invalid}
            aria-describedby={error ? errorId : undefined}
            onChange={(event) => onUnknownChange(event.target.checked)}
          />
          <span className="leading-5">{unknownLabel}</span>
        </label>
      ) : null}
      {error ? (
        <p id={errorId} className="text-sm font-medium text-red-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}
